// Per-order validation panel (Kalpana UAT round 5).
// Renders one card per validation flag with Approve / Reject / Skip
// and inline edit for brand / generic / container. Replaces the earlier
// single-line text in data_quality_notes.
//
// Action defaults agreed with Venky 2026-04-28:
//   Reject = flag dismissed; order proceeds without master update.
//   Skip   = same effect as reject from the order's perspective; flag
//            stays as 'skipped' for revisit later.
//   Case C Approve = admin picks 'update_master' or 'update_order' via
//                    a small dropdown on the card.
//   Edit Brand/Generic/Container = edits the order's data; subsequent
//                                  Approve uses the corrected values.

import { useEffect, useState } from 'react';
import { validationFlagApi } from '../../api/services';
import toast from 'react-hot-toast';
import { CheckCircle2, XCircle, SkipForward, Edit2, Save, AlertTriangle, Loader2 } from 'lucide-react';

const FLAG_LABEL = {
  case_a: 'Case A — Generic exists, brand new',
  case_b: 'Case B — Both brand and generic new',
  case_c: 'Case C — Container mismatch',
  shipper: 'Shipper rule missing',
};

const FLAG_TONE = {
  case_a: 'border-amber-300 bg-amber-50',
  case_b: 'border-red-300 bg-red-50',
  case_c: 'border-purple-300 bg-purple-50',
  shipper: 'border-blue-300 bg-blue-50',
};

export default function ValidationFlagsPanel({ orderId, onResolved }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [edits, setEdits] = useState({}); // flagId → { brand, generic, container, shipper, action }
  const [editing, setEditing] = useState({}); // flagId → bool

  function load() {
    setLoading(true);
    validationFlagApi.forOrder(orderId)
      .then((res) => setFlags(res.data.flags || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load flags'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  async function approve(f) {
    setBusyId(f.id);
    try {
      const editPayload = edits[f.id] || {};
      // For shipper flags require a shipper code.
      if (f.flag_type === 'shipper' && !editPayload.shipper) {
        toast.error('Enter a shipper code (LP / EMS / Airmail) before approving');
        setBusyId(null); return;
      }
      await validationFlagApi.approve(f.id, {
        edit: {
          brand: editPayload.brand,
          generic: editPayload.generic,
          container: editPayload.container,
          shipper: editPayload.shipper,
          note: editPayload.note,
        },
        action: editPayload.action || null, // case_c: update_master | update_order
      });
      toast.success('Approved');
      load();
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approve failed');
    } finally { setBusyId(null); }
  }

  async function reject(f) {
    setBusyId(f.id);
    try {
      const note = window.prompt('Optional reason (or leave empty):', '');
      if (note === null) { setBusyId(null); return; }
      await validationFlagApi.reject(f.id, { note: note || null });
      toast.success('Rejected');
      load(); onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reject failed');
    } finally { setBusyId(null); }
  }

  async function skip(f) {
    setBusyId(f.id);
    try {
      const note = window.prompt('Optional reason (or leave empty):', '');
      if (note === null) { setBusyId(null); return; }
      await validationFlagApi.skip(f.id, { note: note || null });
      toast.success('Skipped');
      load(); onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Skip failed');
    } finally { setBusyId(null); }
  }

  function setEdit(flagId, key, value) {
    setEdits((prev) => ({ ...prev, [flagId]: { ...(prev[flagId] || {}), [key]: value } }));
  }

  if (loading) {
    return <div className="text-sm text-gray-500">
      <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading validation flags…
    </div>;
  }
  if (flags.length === 0) return null;

  const pending = flags.filter((f) => f.status === 'pending');
  const resolved = flags.filter((f) => f.status !== 'pending');

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-600" />
          <span className="font-semibold text-sm">Validation flags</span>
        </div>
        <span className="text-xs text-gray-500">
          {pending.length} pending · {resolved.length} resolved
        </span>
      </div>

      {pending.length === 0 ? (
        <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          ✓ All validation flags on this order are resolved.
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map((f) => {
            const tone = FLAG_TONE[f.flag_type] || 'border-gray-200 bg-white';
            const e = edits[f.id] || {};
            const isEditing = !!editing[f.id];
            return (
              <div key={f.id} className={`border rounded-lg p-3 ${tone}`}>
                <div className="text-xs uppercase font-semibold tracking-wide text-gray-700 mb-1">
                  {FLAG_LABEL[f.flag_type]}
                </div>
                {f.flag_type === 'shipper' ? (
                  <div className="text-sm">
                    Country: <strong>{f.country}</strong> has no shipper rule.
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-gray-600">Allowed shipper:</span>
                      <input value={e.shipper || ''}
                             onChange={(ev) => setEdit(f.id, 'shipper', ev.target.value)}
                             placeholder="LP / EMS / Airmail"
                             className="px-2 py-1 border border-gray-300 rounded text-sm w-32" />
                      <span className="text-xs text-gray-500">
                        — Approve adds (country, shipper) to the rule list.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm space-y-2">
                    {!isEditing ? (
                      <div>
                        Brand: <strong>{f.brand}</strong> · Generic: <strong>{f.generic}</strong>
                        {f.container && <> · Container: <strong>{f.container}</strong></>}
                        {f.flag_type === 'case_c' && f.master_container && (
                          <> · Master: <strong>{f.master_container}</strong></>
                        )}
                        <button onClick={() => {
                          setEdit(f.id, 'brand', f.brand);
                          setEdit(f.id, 'generic', f.generic);
                          setEdit(f.id, 'container', f.container);
                          setEditing((p) => ({ ...p, [f.id]: true }));
                        }}
                          className="ml-2 text-xs text-blue-600 hover:underline">
                          <Edit2 className="w-3 h-3 inline mr-0.5" />Edit
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        <label className="text-xs">
                          <span className="block text-gray-600 mb-0.5">Indian Brand</span>
                          <input value={e.brand || ''}
                                 onChange={(ev) => setEdit(f.id, 'brand', ev.target.value)}
                                 className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                        </label>
                        <label className="text-xs">
                          <span className="block text-gray-600 mb-0.5">Generic Name</span>
                          <input value={e.generic || ''}
                                 onChange={(ev) => setEdit(f.id, 'generic', ev.target.value)}
                                 className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                        </label>
                        <label className="text-xs">
                          <span className="block text-gray-600 mb-0.5">Container</span>
                          <input value={e.container || ''}
                                 onChange={(ev) => setEdit(f.id, 'container', ev.target.value)}
                                 className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                        </label>
                      </div>
                    )}

                    {f.flag_type === 'case_c' && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-600">Approve will:</span>
                        <select value={e.action || 'update_master'}
                                onChange={(ev) => setEdit(f.id, 'action', ev.target.value)}
                                className="px-2 py-1 border border-gray-300 rounded text-xs">
                          <option value="update_master">Update master container ({f.master_container || '—'} → {e.container ?? f.container})</option>
                          <option value="update_order">Update order container ({e.container ?? f.container} → {f.master_container || '—'})</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => approve(f)} disabled={busyId === f.id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve & Map
                  </button>
                  <button onClick={() => reject(f)} disabled={busyId === f.id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-rose-100 text-rose-800 rounded text-xs font-medium hover:bg-rose-200 disabled:opacity-50">
                    <XCircle className="w-3.5 h-3.5" /> Reject
                  </button>
                  <button onClick={() => skip(f)} disabled={busyId === f.id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 disabled:opacity-50">
                    <SkipForward className="w-3.5 h-3.5" /> Skip for now
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {resolved.length > 0 && (
        <details className="mt-3">
          <summary className="text-xs text-gray-500 cursor-pointer">
            {resolved.length} resolved flag(s)
          </summary>
          <ul className="mt-2 text-xs text-gray-600 space-y-1">
            {resolved.map((f) => (
              <li key={f.id} className="flex gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold
                  ${f.status === 'approved' ? 'bg-emerald-100 text-emerald-800'
                    : f.status === 'rejected' ? 'bg-rose-100 text-rose-800'
                    : 'bg-gray-100 text-gray-700'}`}>
                  {f.status}
                </span>
                <span>{FLAG_LABEL[f.flag_type]}</span>
                {f.brand && <span>· {f.brand}/{f.generic}</span>}
                {f.country && <span>· {f.country}</span>}
                {f.resolution_note && <span className="italic text-gray-500">— "{f.resolution_note}"</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
